import React, { useEffect, useState } from 'react';
import { X, ChevronLeft, Sparkles, Wrench, AlertTriangle, Calendar, CheckCircle, Users, Search, Check, MapPin, Star, Clock } from 'lucide-react';
interface NuovoInterventoWizardProps {
  isOpen: boolean;
  onClose: () => void;
}
type Step = 'type' | 'urgency' | 'searching' | 'result' | 'calendar' | 'success';
type InterventionType = 'pulizia' | 'manutenzione' | null;
type UrgencyType = 'urgente' | 'ordinario' | null;
export function NuovoInterventoWizard({
  isOpen,
  onClose
}: NuovoInterventoWizardProps) {
  const [step, setStep] = useState<Step>('type');
  const [type, setType] = useState<InterventionType>(null);
  const [urgency, setUrgency] = useState<UrgencyType>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('type');
      setType(null);
      setUrgency(null);
      setSelectedDate(null);
      setIsConfirming(false);
    }
  }, [isOpen]);
  const handleClose = () => {
    onClose();
  };
  const handleBack = () => {
    if (step === 'urgency') setStep('type');
    if (step === 'calendar') setStep('urgency');
    if (step === 'result') setStep('urgency');
  };
  const handleTypeSelect = (selectedType: InterventionType) => {
    setType(selectedType);
    setStep('urgency');
  };
  const handleUrgencySelect = (selectedUrgency: UrgencyType) => {
    setUrgency(selectedUrgency);
    if (selectedUrgency === 'urgente') {
      setStep('searching');
      // Simulate searching for operators
      setTimeout(() => {
        setStep('result');
      }, 2500);
    } else {
      setStep('calendar');
    }
  };
  const handleConfirm = () => {
    setIsConfirming(true);
    setTimeout(() => {
      setIsConfirming(false);
      setStep('success');
    }, 1500);
  };
  // Mock data for SkyScanner-style calendar
  const calendarDays = [{
    date: '15 Gen',
    dayName: 'Lun',
    price: '€45',
    savings: '0%',
    hosts: 1,
    color: 'gray'
  }, {
    date: '16 Gen',
    dayName: 'Mar',
    price: '€38',
    savings: '15%',
    hosts: 3,
    color: 'yellow'
  }, {
    date: '17 Gen',
    dayName: 'Mer',
    price: '€31',
    savings: '30%',
    hosts: 5,
    color: 'green'
  }, {
    date: '18 Gen',
    dayName: 'Gio',
    price: '€45',
    savings: '0%',
    hosts: 1,
    color: 'gray'
  }, {
    date: '19 Gen',
    dayName: 'Ven',
    price: '€38',
    savings: '15%',
    hosts: 2,
    color: 'yellow'
  }, {
    date: '20 Gen',
    dayName: 'Sab',
    price: '€45',
    savings: '0%',
    hosts: 0,
    color: 'gray'
  }, {
    date: '21 Gen',
    dayName: 'Dom',
    price: '€45',
    savings: '0%',
    hosts: 0,
    color: 'gray'
  }, {
    date: '22 Gen',
    dayName: 'Lun',
    price: '€29',
    savings: '35%',
    hosts: 6,
    color: 'green'
  }, {
    date: '23 Gen',
    dayName: 'Mar',
    price: '€38',
    savings: '15%',
    hosts: 2,
    color: 'yellow'
  }, {
    date: '24 Gen',
    dayName: 'Mer',
    price: '€45',
    savings: '0%',
    hosts: 1,
    color: 'gray'
  }];
  const getHeaderTitle = () => {
    switch (step) {
      case 'type':
        return 'Nuovo Intervento';
      case 'urgency':
        return 'Seleziona Urgenza';
      case 'searching':
        return 'Ricerca Operatori...';
      case 'result':
        return 'Operatore Trovato';
      case 'calendar':
        return 'Pianifica Intervento';
      case 'success':
        return 'Intervento Confermato!';
      default:
        return 'Nuovo Intervento';
    }
  };
  const getStepNumber = () => {
    switch (step) {
      case 'type':
        return 1;
      case 'urgency':
        return 2;
      case 'searching':
        return 3;
      case 'result':
        return 3;
      case 'calendar':
        return 3;
      case 'success':
        return 4;
      default:
        return 1;
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200" onClick={handleClose} data-id="element-136">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()} data-id="element-137">
        {/* Header */}
        <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-200" data-id="element-138">
          <div className="flex items-center gap-3" data-id="element-139">
            {step !== 'type' && step !== 'searching' && step !== 'success' && <button onClick={handleBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors -ml-2" data-id="element-140">
                <ChevronLeft size={20} className="text-gray-500" data-id="element-141" />
              </button>}
            <div data-id="element-142">
              <h2 className="text-lg font-bold text-navy" data-id="element-143">
                {getHeaderTitle()}
              </h2>
              {step !== 'success' && step !== 'searching' && <p className="text-xs text-gray-500 mt-0.5" data-id="element-144">
                  Passo {getStepNumber()} di 3
                </p>}
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-145">
            <X size={20} className="text-gray-500" data-id="element-146" />
          </button>
        </div>

        {/* Progress Bar */}
        {step !== 'success' && step !== 'searching' && <div className="px-4 lg:px-6 pt-4" data-id="element-147">
            <div className="flex gap-2" data-id="element-148">
              {[1, 2, 3].map(s => <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${s <= getStepNumber() ? 'bg-teal-deep' : 'bg-gray-200'}`} data-id="element-149" />)}
            </div>
          </div>}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-150">
          {/* STEP 1: Type Selection */}
          {step === 'type' && <div className="space-y-4" data-id="element-151">
              <div data-id="element-152">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-153">
                  Che tipo di intervento ti serve?
                </h3>
                <p className="text-xs text-gray-500" data-id="element-154">
                  Seleziona la categoria dell'intervento
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3" data-id="element-155">
                <button onClick={() => handleTypeSelect('pulizia')} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-teal-deep hover:bg-teal-50/50 transition-all group" data-id="element-156">
                  <div className="flex items-start gap-4" data-id="element-157">
                    <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0" data-id="element-158">
                      <Sparkles size={24} className="text-cyan-600" data-id="element-159" />
                    </div>
                    <div className="flex-1" data-id="element-160">
                      <span className="text-sm font-semibold text-navy group-hover:text-teal-deep transition-colors" data-id="element-161">
                        Intervento di Pulizia
                      </span>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-162">
                        Pulizie ordinarie, deep cleaning, o pulizie
                        post-soggiorno
                      </p>
                    </div>
                  </div>
                </button>

                <button onClick={() => handleTypeSelect('manutenzione')} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-teal-deep hover:bg-teal-50/50 transition-all group" data-id="element-163">
                  <div className="flex items-start gap-4" data-id="element-164">
                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-165">
                      <Wrench size={24} className="text-amber-600" data-id="element-166" />
                    </div>
                    <div className="flex-1" data-id="element-167">
                      <span className="text-sm font-semibold text-navy group-hover:text-teal-deep transition-colors" data-id="element-168">
                        Intervento di Manutenzione
                      </span>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-169">
                        Riparazioni, idraulica, elettricista o altri lavori
                        tecnici
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>}

          {/* STEP 2: Urgency Selection */}
          {step === 'urgency' && <div className="space-y-4" data-id="element-170">
              <div data-id="element-171">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-172">
                  Quanto è urgente?
                </h3>
                <p className="text-xs text-gray-500" data-id="element-173">
                  Seleziona la priorità dell'intervento
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3" data-id="element-174">
                <button onClick={() => handleUrgencySelect('urgente')} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-red-500 hover:bg-red-50/50 transition-all group" data-id="element-175">
                  <div className="flex items-start gap-4" data-id="element-176">
                    <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0" data-id="element-177">
                      <AlertTriangle size={24} className="text-red-600" data-id="element-178" />
                    </div>
                    <div className="flex-1" data-id="element-179">
                      <div className="flex items-center gap-2" data-id="element-180">
                        <span className="text-sm font-semibold text-navy group-hover:text-red-600 transition-colors" data-id="element-181">
                          Urgente (ASAP)
                        </span>
                        <span className="text-[10px] font-medium px-2 py-0.5 bg-red-100 text-red-700 rounded-full" data-id="element-182">
                          Sovrapprezzo
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-183">
                        Cercheremo il primo operatore disponibile nelle
                        vicinanze. Ideale per emergenze.
                      </p>
                    </div>
                  </div>
                </button>

                <button onClick={() => handleUrgencySelect('ordinario')} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-teal-deep hover:bg-teal-50/50 transition-all group" data-id="element-184">
                  <div className="flex items-start gap-4" data-id="element-185">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-186">
                      <Calendar size={24} className="text-blue-600" data-id="element-187" />
                    </div>
                    <div className="flex-1" data-id="element-188">
                      <div className="flex items-center gap-2" data-id="element-189">
                        <span className="text-sm font-semibold text-navy group-hover:text-teal-deep transition-colors" data-id="element-190">
                          Ordinario (Pianificato)
                        </span>
                        <span className="text-[10px] font-medium px-2 py-0.5 bg-green-100 text-green-700 rounded-full" data-id="element-191">
                          Risparmia
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-192">
                        Scegli una data. Risparmia aggregandoti agli interventi
                        di altri Host nella tua zona.
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>}

          {/* STEP 3A: Searching Animation */}
          {step === 'searching' && <div className="text-center py-12" data-id="element-193">
              <div className="relative w-24 h-24 mx-auto mb-6" data-id="element-194">
                <div className="absolute inset-0 border-4 border-gray-100 rounded-full" data-id="element-195"></div>
                <div className="absolute inset-0 border-4 border-teal-deep rounded-full border-t-transparent animate-spin" data-id="element-196"></div>
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-197">
                  <Search size={32} className="text-teal-deep animate-pulse" data-id="element-198" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-navy mb-2" data-id="element-199">
                Ricerca in corso...
              </h3>
              <p className="text-sm text-gray-500 max-w-[250px] mx-auto" data-id="element-200">
                Stiamo cercando il primo operatore disponibile per il tuo
                intervento urgente.
              </p>
            </div>}

          {/* STEP 3A: Urgent Result */}
          {step === 'result' && <div className="space-y-6" data-id="element-201">
              <div className="text-center" data-id="element-202">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-203">
                  <CheckCircle size={32} className="text-green-600" data-id="element-204" />
                </div>
                <h3 className="text-lg font-bold text-navy mb-1" data-id="element-205">
                  Operatore Trovato!
                </h3>
                <p className="text-sm text-gray-500" data-id="element-206">
                  Un operatore è disponibile per intervenire a breve.
                </p>
              </div>

              <div className="bg-white border-2 border-teal-deep rounded-xl p-4 shadow-sm relative overflow-hidden" data-id="element-207">
                <div className="absolute top-0 right-0 bg-teal-deep text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider" data-id="element-208">
                  Disponibile Ora
                </div>

                <div className="flex items-start gap-4 mt-2" data-id="element-209">
                  <img src="https://ui-avatars.com/api/?name=Marco+R&background=0D9488&color=fff&size=64" alt="Operatore" className="w-16 h-16 rounded-full border-2 border-gray-100" data-id="element-210" />
                  <div className="flex-1" data-id="element-211">
                    <h4 className="text-base font-bold text-navy" data-id="element-212">
                      Marco Rossi
                    </h4>
                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-1" data-id="element-213">
                      <Star size={12} className="text-yellow-400 fill-current" data-id="element-214" />
                      <span className="font-medium text-gray-700" data-id="element-215">4.9</span>
                      <span data-id="element-216">(124 recensioni)</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-1" data-id="element-217">
                      <MapPin size={12} data-id="element-218" />
                      <span data-id="element-219">A 2.5 km da te</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-1" data-id="element-220">
                      <Clock size={12} data-id="element-221" />
                      <span className="font-medium text-teal-deep" data-id="element-222">
                        Arrivo stimato: 45 min
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center" data-id="element-223">
                  <div data-id="element-224">
                    <div className="text-xs text-gray-500" data-id="element-225">Costo stimato</div>
                    <div className="text-lg font-bold text-navy" data-id="element-226">€65,00</div>
                  </div>
                  <div className="text-right" data-id="element-227">
                    <div className="text-xs text-gray-500" data-id="element-228">
                      Incluso sovrapprezzo urgenza
                    </div>
                  </div>
                </div>
              </div>

              {/* Urgent Result - Accept button */}
              <button onClick={handleConfirm} disabled={isConfirming} className={`w-full px-4 py-3 rounded-full text-sm font-medium transition-colors min-h-[48px] flex items-center justify-center gap-2 ${!isConfirming ? 'bg-teal-deep text-white hover:bg-[#50BFD9]' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-229">
                {isConfirming ? <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-230" />
                    Conferma in corso...
                  </> : <>
                    <CheckCircle size={18} data-id="element-231" />
                    Accetta Intervento
                  </>}
              </button>
            </div>}

          {/* STEP 3B: SkyScanner Calendar */}
          {step === 'calendar' && <div className="space-y-4" data-id="element-232">
              <div data-id="element-233">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-234">
                  Scegli la data e risparmia
                </h3>
                <p className="text-xs text-gray-500" data-id="element-235">
                  Aggregati agli interventi di altri Host nella tua zona per
                  ottenere sconti.
                </p>
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 text-[10px] font-medium text-gray-500 bg-gray-50 p-2 rounded-lg" data-id="element-236">
                <div className="flex items-center gap-1.5" data-id="element-237">
                  <div className="w-3 h-3 rounded-full bg-green-100 border border-green-300" data-id="element-238"></div>
                  <span data-id="element-239">Alto risparmio</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-240">
                  <div className="w-3 h-3 rounded-full bg-yellow-100 border border-yellow-300" data-id="element-241"></div>
                  <span data-id="element-242">Medio risparmio</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-243">
                  <div className="w-3 h-3 rounded-full bg-gray-100 border border-gray-300" data-id="element-244"></div>
                  <span data-id="element-245">Prezzo base</span>
                </div>
              </div>

              {/* Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-246">
                {calendarDays.map((day, idx) => {
              const isSelected = selectedDate === day.date;
              let bgColor = 'bg-white';
              let borderColor = 'border-gray-200';
              let textColor = 'text-navy';
              let savingsColor = 'text-gray-400';
              if (isSelected) {
                bgColor = 'bg-teal-50';
                borderColor = 'border-teal-deep';
              } else if (day.color === 'green') {
                bgColor = 'bg-green-50/50';
                borderColor = 'border-green-200';
                savingsColor = 'text-green-600';
              } else if (day.color === 'yellow') {
                bgColor = 'bg-yellow-50/50';
                borderColor = 'border-yellow-200';
                savingsColor = 'text-yellow-600';
              }
              return <button key={idx} onClick={() => setSelectedDate(day.date)} className={`relative flex flex-col p-3 rounded-xl border-2 transition-all text-left hover:border-teal-deep ${bgColor} ${borderColor}`} data-id="element-247">
                      {isSelected && <div className="absolute -top-2 -right-2 w-5 h-5 bg-teal-deep rounded-full flex items-center justify-center border-2 border-white" data-id="element-248">
                          <Check size={12} className="text-white" data-id="element-249" />
                        </div>}

                      <div className="flex justify-between items-start mb-2" data-id="element-250">
                        <div data-id="element-251">
                          <span className="text-[10px] font-medium text-gray-500 uppercase block" data-id="element-252">
                            {day.dayName}
                          </span>
                          <span className={`text-sm font-bold ${textColor}`} data-id="element-253">
                            {day.date}
                          </span>
                        </div>
                        <span className={`text-sm font-bold ${textColor}`} data-id="element-254">
                          {day.price}
                        </span>
                      </div>

                      <div className="mt-auto pt-2 border-t border-gray-100/50" data-id="element-255">
                        {day.hosts > 0 ? <div className="flex flex-col gap-0.5" data-id="element-256">
                            <span className={`text-[10px] font-bold ${savingsColor}`} data-id="element-257">
                              -{day.savings} di sconto
                            </span>
                            <span className="text-[9px] text-gray-500 flex items-center gap-1" data-id="element-258">
                              <Users size={8} data-id="element-259" /> {day.hosts} Host aggregati
                            </span>
                          </div> : <div className="flex flex-col gap-0.5" data-id="element-260">
                            <span className="text-[10px] font-medium text-gray-400" data-id="element-261">
                              Prezzo standard
                            </span>
                            <span className="text-[9px] text-gray-400" data-id="element-262">
                              Nessun Host aggregato
                            </span>
                          </div>}
                      </div>
                    </button>;
            })}
              </div>

              {/* Calendar - Confirm Date button */}
              <button onClick={handleConfirm} disabled={!selectedDate || isConfirming} className={`w-full mt-4 px-4 py-3 rounded-full text-sm font-medium transition-colors min-h-[48px] flex items-center justify-center gap-2 ${selectedDate && !isConfirming ? 'bg-teal-deep text-white hover:bg-[#50BFD9]' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-263">
                {isConfirming ? <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-264" />
                    Conferma in corso...
                  </> : <>
                    <CheckCircle size={18} data-id="element-265" />
                    Conferma Data
                  </>}
              </button>
            </div>}

          {/* STEP 4: Success */}
          {step === 'success' && <div className="text-center py-6" data-id="element-266">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-267">
                <CheckCircle size={32} className="text-green-600" data-id="element-268" />
              </div>
              <h3 className="text-lg font-bold text-navy mb-2" data-id="element-269">
                Intervento Confermato!
              </h3>
              <p className="text-sm text-gray-600 mb-6" data-id="element-270">
                Il tuo intervento è stato programmato con successo.
              </p>

              <div className="bg-gray-50 rounded-xl p-4 text-left space-y-2 mb-6" data-id="element-271">
                <div className="flex justify-between text-sm" data-id="element-272">
                  <span className="text-gray-600" data-id="element-273">Tipo</span>
                  <span className="font-medium text-navy capitalize" data-id="element-274">
                    {type}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-275">
                  <span className="text-gray-600" data-id="element-276">Urgenza</span>
                  <span className="font-medium text-navy capitalize" data-id="element-277">
                    {urgency}
                  </span>
                </div>
                {urgency === 'ordinario' && selectedDate && <div className="flex justify-between text-sm" data-id="element-278">
                    <span className="text-gray-600" data-id="element-279">Data</span>
                    <span className="font-medium text-navy" data-id="element-280">
                      {selectedDate}
                    </span>
                  </div>}
                {urgency === 'urgente' && <div className="flex justify-between text-sm" data-id="element-281">
                    <span className="text-gray-600" data-id="element-282">Operatore</span>
                    <span className="font-medium text-navy" data-id="element-283">Marco Rossi</span>
                  </div>}
              </div>

              {/* Success - Close button */}
              <button onClick={handleClose} className="w-full px-4 py-3 bg-teal-deep text-white rounded-full text-sm font-medium hover:bg-[#50BFD9] transition-colors min-h-[48px]" data-id="element-284">
                Chiudi
              </button>
            </div>}
        </div>
      </div>
    </div>;
}